/**
 * coπ's voices (Gordon 2026-11-01: "give users a choice of all these voices in
 * the profile settings"). '' = the device's own text-to-speech (free, works
 * offline). Anything else is a Gemini TTS voice: the 30 studio voices below
 * speak every language; the library (GET /api/copi/voices) adds ~2,000 voices
 * that each belong to one language/accent.
 *
 * coπ only ever speaks FIXED lines (COPI_VOICE_LINES, translated in the
 * `copi` namespace), so each voice × language × line is rendered once by
 * /api/copi/say and kept in Storage — no cost per use.
 */

export type VoiceGender = 'female' | 'male' | 'neutral';
export type VoicePitch = 'low' | 'medium' | 'high';

export interface CopiVoice {
  id: string;
  name: string;
  gender: VoiceGender;
  pitch: VoicePitch;
  /** Studio voice: one-word character, a key under `copiVoice.style`. */
  style?: string;
  /** Library voice: the language/accent/persona it was made for (English, from Google). */
  lang?: string;
  accent?: string;
  persona?: string;
  description?: string;
}

export const MAIN_VOICES: CopiVoice[] = [
  { id: 'zephyr', name: 'Zephyr', style: 'bright', gender: 'female', pitch: 'high' },
  { id: 'puck', name: 'Puck', style: 'upbeat', gender: 'male', pitch: 'low' },
  { id: 'charon', name: 'Charon', style: 'informative', gender: 'male', pitch: 'low' },
  { id: 'kore', name: 'Kore', style: 'firm', gender: 'female', pitch: 'medium' },
  { id: 'fenrir', name: 'Fenrir', style: 'excitable', gender: 'male', pitch: 'low' },
  { id: 'leda', name: 'Leda', style: 'youthful', gender: 'female', pitch: 'high' },
  { id: 'orus', name: 'Orus', style: 'firm', gender: 'male', pitch: 'low' },
  { id: 'aoede', name: 'Aoede', style: 'breezy', gender: 'female', pitch: 'medium' },
  { id: 'callirrhoe', name: 'Callirrhoe', style: 'easyGoing', gender: 'female', pitch: 'medium' },
  { id: 'autonoe', name: 'Autonoe', style: 'bright', gender: 'female', pitch: 'medium' },
  { id: 'enceladus', name: 'Enceladus', style: 'breathy', gender: 'male', pitch: 'low' },
  { id: 'iapetus', name: 'Iapetus', style: 'clear', gender: 'male', pitch: 'low' },
  { id: 'umbriel', name: 'Umbriel', style: 'easyGoing', gender: 'male', pitch: 'low' },
  { id: 'algieba', name: 'Algieba', style: 'smooth', gender: 'male', pitch: 'low' },
  { id: 'despina', name: 'Despina', style: 'smooth', gender: 'female', pitch: 'medium' },
  { id: 'erinome', name: 'Erinome', style: 'clear', gender: 'female', pitch: 'medium' },
  { id: 'algenib', name: 'Algenib', style: 'gravelly', gender: 'male', pitch: 'low' },
  { id: 'rasalgethi', name: 'Rasalgethi', style: 'informative', gender: 'male', pitch: 'medium' },
  { id: 'laomedeia', name: 'Laomedeia', style: 'upbeat', gender: 'female', pitch: 'high' },
  { id: 'achernar', name: 'Achernar', style: 'soft', gender: 'female', pitch: 'high' },
  { id: 'alnilam', name: 'Alnilam', style: 'firm', gender: 'male', pitch: 'low' },
  { id: 'schedar', name: 'Schedar', style: 'even', gender: 'male', pitch: 'low' },
  { id: 'gacrux', name: 'Gacrux', style: 'mature', gender: 'female', pitch: 'medium' },
  { id: 'pulcherrima', name: 'Pulcherrima', style: 'forward', gender: 'neutral', pitch: 'medium' },
  { id: 'achird', name: 'Achird', style: 'friendly', gender: 'male', pitch: 'low' },
  { id: 'zubenelgenubi', name: 'Zubenelgenubi', style: 'casual', gender: 'male', pitch: 'low' },
  { id: 'vindemiatrix', name: 'Vindemiatrix', style: 'gentle', gender: 'female', pitch: 'medium' },
  { id: 'sadachbia', name: 'Sadachbia', style: 'lively', gender: 'male', pitch: 'low' },
  { id: 'sadaltager', name: 'Sadaltager', style: 'knowledgeable', gender: 'male', pitch: 'medium' },
  { id: 'sulafat', name: 'Sulafat', style: 'warm', gender: 'female', pitch: 'medium' },
];

export const MAIN_VOICE_IDS = new Set(MAIN_VOICES.map(v => v.id));

/** The lines coπ can say with a Gemini voice — keys in the `copi` namespace. */
export const COPI_VOICE_LINES = ['whatDidYouMean', 'voicePreview', 'newMessage', 'readOrSelf'] as const;
export type CopiVoiceLine = (typeof COPI_VOICE_LINES)[number];

/** A Gemini voice id as the API names them ("sulafat", "fr-fr-advisor-3"). */
export const copiVoiceIdOk = (id: unknown): id is string => typeof id === 'string' && /^[a-z0-9][a-z0-9-]{1,63}$/.test(id);
